import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarCheck, FileSignature, HeartHandshake, MapPinned, MessageSquareText, ReceiptText, ShieldCheck, UserRound, Wallet } from "lucide-react";
import { RewardsBadge } from "./Rewards";
import { Field, Input, Select } from "@/components/kmt/ui";
import { TimeSelect } from "@/components/kmt/TimeSelect";
import { SERVICES, todayCentral } from "@/lib/format";

const AREAS = ["DeSoto County", "Greater Memphis", "Oxford", "Nashville"];
const STEPS = [
  { icon: MessageSquareText, title: "Request", body: "Tell us where and when. Name and phone are all we need." },
  { icon: CalendarCheck, title: "Get your quote", body: "Kyia reviews your trip and texts you a clear quote." },
  { icon: FileSignature, title: "Sign once", body: "First ride? Review and sign the Master Agreement from your phone." },
  { icon: ShieldCheck, title: "Confirm", body: "Send your payment and your ride is locked in." },
];
const WHY = [
  { icon: HeartHandshake, title: "A familiar face", body: "Every request is handled by Kyia personally." },
  { icon: ReceiptText, title: "Clear quotes", body: "You see your full quote by text before anything is booked." },
  { icon: Wallet, title: "Easy payment", body: "Zelle, Apple Pay, Venmo, PayPal or Cash." },
  { icon: MapPinned, title: "Local and long distance", body: "From a ride across town to the drive to Nashville." },
];

function QuickRequest() {
  const nav = useNavigate();
  const [f, setF] = useState({ service: "standard", pickup_address: "", dropoff_address: "", pickup_date: "", pickup_time: "" });
  const go = (e: React.FormEvent) => {
    e.preventDefault();
    nav(`/book?${new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString()}`);
  };
  return (
    <form onSubmit={go} className="rise rounded-md border border-gold/40 bg-aubergine/90 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.5)] backdrop-blur-xl md:p-8" style={{ animationDelay: "240ms" }} data-testid="hero-quick-request">
      <p className="eyebrow">Request a ride</p>
      <p className="mt-2 font-display text-2xl text-mist">Where are we going?</p>
      <div className="gold-rule my-5" />
      <div className="grid gap-4">
        <Field label="Pickup address" htmlFor="qr-from"><Input id="qr-from" value={f.pickup_address} onChange={(e) => setF({ ...f, pickup_address: e.target.value })} data-testid="quick-pickup" /></Field>
        <Field label="Drop-off address" htmlFor="qr-to"><Input id="qr-to" value={f.dropoff_address} onChange={(e) => setF({ ...f, dropoff_address: e.target.value })} data-testid="quick-dropoff" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Service" htmlFor="qr-svc"><Select id="qr-svc" options={SERVICES} value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="quick-service" /></Field>
          <Field label="Pickup date" htmlFor="qr-date"><Input id="qr-date" type="date" min={todayCentral()} value={f.pickup_date} onChange={(e) => setF({ ...f, pickup_date: e.target.value })} data-testid="quick-date" /></Field>
        </div>
        <Field label="Pickup time (Central)" htmlFor="qr-time"><TimeSelect id="qr-time" value={f.pickup_time} onChange={(v) => setF({ ...f, pickup_time: v })} testid="quick-time" /></Field>
        <button type="submit" className="mt-2 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-plum font-medium text-mist transition-colors duration-200 hover:bg-orchid" data-testid="quick-continue-btn">
          Continue request <ArrowRight size={18} />
        </button>
        <p className="text-[14px] text-mist/65">No payment now. You'll get a quote by text.</p>
      </div>
    </form>
  );
}

export default function Home() {
  return (
    <>
      <section className="grain relative isolate overflow-hidden" data-testid="home-hero">
        <img src="/images/nashville.jpg" alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-ink via-ink/90 to-ink/60" />
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 md:px-10 lg:min-h-[82vh] lg:grid-cols-[1.15fr_1fr]">
          <div>
            <p className="eyebrow rise">KMT Private Rides</p>
            <h1 className="rise mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.1] text-gold [text-shadow:0_2px_24px_rgba(0,0,0,0.6)] sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }} data-testid="home-hero-headline">
              More Than a Ride. An Experience.
            </h1>
            <div className="gold-rule rise mt-8 w-40" style={{ animationDelay: "140ms" }} />
            <p className="rise mt-8 max-w-xl text-base text-mist md:text-lg" style={{ animationDelay: "200ms" }} data-testid="home-hero-subline">
              A Familiar Face. A Personal Touch. A Better Ride.
            </p>
            <div className="rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: "260ms" }}>
              <Link to="/book" data-testid="hero-book-btn" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-plum px-8 font-medium text-mist transition-colors duration-200 hover:bg-orchid">
                Book a Ride <ArrowRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-services-btn" className="inline-flex min-h-[52px] items-center rounded-full border border-gold/60 px-8 text-gold transition-colors duration-200 hover:bg-gold/10">
                Explore services
              </Link>
            </div>
          </div>
          <QuickRequest />
        </div>
      </section>

      <section className="border-b border-gold/20 bg-aubergine">
        <ul className="mx-auto grid max-w-7xl grid-cols-2 md:grid-cols-4" data-testid="service-areas">
          {AREAS.map((a, i) => (
            <li key={a} className={`flex min-h-[96px] items-center gap-3 px-5 py-6 md:px-10 ${i ? "md:border-l md:border-gold/20" : ""}`}>
              <span className="font-display text-[14px] text-gold">0{i + 1}</span>
              <span className="font-display text-lg text-mist">{a}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-10">
        <div className="text-center">
          <p className="eyebrow">How it works</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl">Four simple steps</h2>
        </div>
        <ol className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <li key={s.title} className="rise group relative rounded-md border border-gold/25 bg-aubergine/60 p-7 transition-colors duration-200 hover:border-gold/70" style={{ animationDelay: `${i * 90}ms` }} data-testid={`how-step-${i}`}>
              <span className="absolute right-6 top-5 font-display text-4xl text-gold/25">0{i + 1}</span>
              <span className="grid h-14 w-14 place-items-center rounded-full bg-plum text-gold transition-colors duration-200 group-hover:bg-orchid"><s.icon size={24} /></span>
              <h3 className="mt-6 font-display text-xl text-mist">{s.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-mist/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-gold/20 bg-aubergine/60">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 md:px-10 lg:grid-cols-2">
          <div className="grid aspect-[5/4] w-full place-items-center rounded-md border border-dashed border-gold/50 bg-ink/60 text-center" data-testid="home-about-photo-placeholder">
            <div className="p-6"><UserRound className="mx-auto text-gold/70" size={48} /><p className="mt-3 text-[14px] text-mist/70">[Photo of Kyia — placeholder]</p></div>
          </div>
          <div>
            <p className="eyebrow">About KMT</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl">A familiar face behind every ride.</h2>
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-mist/80">
              KMT Private Rides is owned and run by Kyia. Every request is read, priced and confirmed by her personally, so you always know who you're riding with.
            </p>
            <Link to="/about" data-testid="home-about-link" className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full border border-gold/60 px-7 text-gold transition-colors duration-200 hover:bg-gold/10">More about us <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-10">
        <p className="eyebrow">Why ride with KMT</p>
        <h2 className="mt-4 font-display text-3xl md:text-4xl">Personal, simple, clear.</h2>
        <div className="mt-14 grid gap-px overflow-hidden rounded-md border border-gold/25 bg-gold/25 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <div key={w.title} className="bg-ink p-8" data-testid={`why-card-${i}`}>
              <w.icon className="text-gold" size={28} />
              <h3 className="mt-5 font-display text-lg text-mist">{w.title}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-mist/75">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-gold/20 bg-aubergine">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 md:grid-cols-[auto_1fr_auto] md:px-10">
          <RewardsBadge />
          <div>
            <p className="font-script text-4xl text-gold">Royal Rewards</p>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-mist/80">A thank-you program for riders who keep choosing KMT.</p>
            <Link to="/royal-rewards" data-testid="home-rewards-link" className="mt-4 inline-flex min-h-[48px] items-center gap-2 text-gold hover:underline">Learn more <ArrowRight size={16} /></Link>
          </div>
          <Link to="/book" data-testid="home-cta-book-btn" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-plum px-8 font-medium text-mist transition-colors duration-200 hover:bg-orchid">Book a Ride <ArrowRight size={18} /></Link>
        </div>
      </section>
    </>
  );
}
